import { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Frame, TitleBar, Button, Checkbox } from "@react95/core";
import { Dropdown, Fieldset, Input, Tab, Tabs } from "@react95/core";

import { Qfecheck111 } from "@react95/icons";
import { useApp, useMessageListener } from "../../context/AppContext";

import { WINDOW_NAMES } from "../../config/const";
import { getModalSizeProps } from "../../config/window";

// interface Quest {
//   id: string;
//   title: string;
//   description: string;
//   status: "not_started" | "in_progress" | "completed";
// }

export const QuestWindowDef = {
  id: WINDOW_NAMES.QuestLog,
  title: "Quest Log",
  icon: <Qfecheck111 variant="32x32_4" />,
  hasButton: true,
};

export default function QuestModal() {
  const { closeModal, sessionReady, sendMessage } = useApp();
  const icon = QuestWindowDef.icon;
  const sizeProps = useMemo(() => getModalSizeProps("auto", "350px", "normal"), []);

  const [loading, setLoading] = useState<boolean>(false);
  const [questData, setQuestData] = useState<any>(null);
  const hasFetchedRef = useRef(false);

  // listen for quest list responses
  useMessageListener("quest_list", (message: any) => {
    if (
      message &&
      message.service === "quest" &&
      message.payload &&
      Array.isArray(message.payload.quests)
    ) {
      console.debug("Received quest list:", message.payload.quests);
      setQuestData(message.payload.quests);
      setLoading(false);
    }
  });

  // fetch quests once when modal is ready
  useEffect(() => {
    if (!sessionReady || hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    setLoading(true);
    sendMessage("quest", "get_quests", {});
  }, [sessionReady, sendMessage]);

  // deduplicate quests and compute topic lists
  const uniqueQuests = useMemo(() => {
    if (!questData) return [];
    const seen = new Set<string>();
    return questData.filter((q: any) => {
      if (!q || !q.id) return false;
      if (seen.has(q.id)) return false;
      seen.add(q.id);
      return true;
    });
  }, [questData]);

  const introQuests = useMemo(
    () => uniqueQuests.filter((q: any) => q.questline === "intro"),
    [uniqueQuests],
  );
  const ctfQuests = useMemo(
    () => uniqueQuests.filter((q: any) => q.questline === "ctf"),
    [uniqueQuests],
  );
  const secretQuests = useMemo(
    () => uniqueQuests.filter((q: any) => q.questline === "secret"),
    [uniqueQuests],
  );

  // calculate completed count from deduped list
  const completedCount = uniqueQuests.filter((q: any) => q.status == "completed").length;

  // only show the secret tab if they have started or completed a secret quest
  const showSecretTab = secretQuests.some((q: any) => q.status && q.status !== "not_started");

  return (
    <>
      <Modal
        key={QuestWindowDef.id}
        id={QuestWindowDef.id}
        icon={icon}
        title={QuestWindowDef.title}
        dragOptions={sizeProps.dragOptions}
        titleBarOptions={[
          <Modal.Minimize key="minimize" />,
          <TitleBar.Close key="close" onClick={() => closeModal(QuestWindowDef.id)} />,
        ]}
        defaultClosed={true}
        height={sizeProps.height}
        width={sizeProps.width}
      >
        <div style={{ padding: "4px" }}>
          {loading && <div>Loading quests...</div>}

          {!loading &&
            (() => {
              // build tab children explicitly to avoid passing falsy children into `Tabs`
              const introTab = (
                <Tab key="intro" title="Intro">
                  <Frame
                    boxShadow="none"
                    // padding="6px"
                    bg="$material"
                    style={{
                      flex: 1,
                    }}
                  >
                    <Frame
                      boxShadow="$in"
                      padding="6px"
                      bg="$material"
                      style={{
                        flex: 1,
                        overflowY: "auto",
                        minHeight: "300px",
                      }}
                    >
                      {introQuests.map((quest) => <QuestItem key={quest.id} quest={quest} />)}
                    </Frame>
                  </Frame>
                </Tab>
              );

              const ctfTab = (
                <Tab key="ctf" title="CTF">
                  <Frame
                    boxShadow="none"
                    bg="$material"
                    style={{
                      flex: 1,
                    }}
                  >
                    <Frame
                      boxShadow="$in"
                      padding="6px"
                      bg="$material"
                      style={{
                        flex: 1,
                        overflowY: "auto",
                        minHeight: "300px",
                      }}
                    >
                      {ctfQuests.map((quest) => (
                        <QuestItem key={quest.id} quest={quest} />
                      ))}
                    </Frame>
                  </Frame>
                </Tab>
              );

              const secretTab = (
                <Tab key="secret" title="Secret">
                  <Frame
                    boxShadow="none"
                    bg="$material"
                    style={{
                      flex: 1,
                    }}
                  >
                    <Frame
                      boxShadow="$in"
                      padding="6px"
                      bg="$material"
                      style={{
                        flex: 1,
                        overflowY: "auto",
                        minHeight: "300px",
                      }}
                    >
                      {secretQuests.map((quest) => <QuestItem key={quest.id} quest={quest} />)}
                    </Frame>
                  </Frame>
                </Tab>
              );

              const tabChildren = [introTab, ctfTab];
              if (showSecretTab) tabChildren.push(secretTab);

              return (
                <>
                  <div style={{ padding: "8px" }}>
                    <div
                      style={{
                        margin: 0,
                        fontSize: "15px",
                        paddingBottom: "6px",
                        fontStyle: "bold",
                        color: "#000",
                      }}
                    >
                      Complete Quests to unlock new ones.
                    </div>
                    <div
                      style={{
                        margin: 0,
                        fontSize: "13px",
                        marginBottom: "8px",
                        color: "#333",
                        paddingLeft: "4px",
                      }}
                    >
                      Current Quest Points: {completedCount}.
                    </div>
                  </div>
                  <Tabs defaultActiveTab="Intro">{tabChildren}</Tabs>
                </>
              );
            })()}
        </div>
      </Modal>
    </>
  );
}

function QuestItem(props: { quest: any }) {
  const { quest } = props;
  return (
    <Frame
      key={quest.id}
      bg="$material"
      boxShadow="in"
      padding="0.75rem"
      style={{
        opacity: quest.status == "completed" ? 0.6 : 1,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        <Checkbox
          checked={quest.status == "completed"}
          readOnly
          style={{ marginRight: "0.75rem", marginTop: "2px" }}
        />
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontWeight: "bold",
              fontSize: "13px",
              marginBottom: "0.25rem",
              textDecoration: quest.status == "completed" ? "line-through" : "none",
            }}
          >
            {quest.title}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#666",
            }}
          >
            {quest.description}
          </div>
        </div>
      </div>
    </Frame>
  );
}
